import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { BRAND } from "../lib/brand";
import { Lock, Mail } from "lucide-react";

function formatErr(detail) {
    if (detail == null) return "Something went wrong.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
    if (detail?.msg) return detail.msg;
    return String(detail);
}

export default function AdminLogin() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await login(email, password);
            navigate("/admin");
        } catch (err) {
            setError(formatErr(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-meraki-bg flex items-center justify-center px-6 py-12 film-grain">
            <div className="w-full max-w-md">
                <Link to="/" data-testid="login-back-link" className="text-[10px] uppercase tracking-[0.3em] text-meraki-muted hover:text-meraki-gold transition-colors">
                    ← Back to site
                </Link>
                <div className="mt-6 border border-meraki-gold/20 bg-meraki-surface p-10">
                    <div className="flex items-center gap-4 mb-8">
                        <img src={BRAND.logo} alt="" className="w-14 h-14 object-contain" />
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.3em] text-meraki-gold">Admin</div>
                            <div className="font-display text-2xl text-meraki-text uppercase leading-none mt-1">Sign In</div>
                        </div>
                    </div>

                    <form onSubmit={onSubmit} className="space-y-5">
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.25em] text-meraki-muted mb-2">Username</label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-meraki-muted" />
                                <input
                                    data-testid="login-email-input"
                                    type="text"
                                    autoComplete="username"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full bg-black border border-meraki-gold/20 px-10 py-3 text-meraki-text placeholder:text-meraki-muted focus:outline-none focus:border-meraki-gold"
                                    placeholder="check with Aditya"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.25em] text-meraki-muted mb-2">Password</label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-meraki-muted" />
                                <input
                                    data-testid="login-password-input"
                                    type="password"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full bg-black border border-meraki-gold/20 px-10 py-3 text-meraki-text placeholder:text-meraki-muted focus:outline-none focus:border-meraki-gold"
                                    placeholder="••••••••"
                                />
                            </div>
                        </div>

                        {error && (
                            <div data-testid="login-error" className="text-xs text-red-400 border border-red-900/50 bg-red-950/30 px-4 py-3">
                                {error}
                            </div>
                        )}

                        <button
                            data-testid="login-submit-btn"
                            type="submit"
                            disabled={loading}
                            className="w-full bg-meraki-gold text-black py-4 text-xs uppercase tracking-[0.3em] font-bold hover:bg-meraki-gold-hover transition-colors disabled:opacity-50"
                        >
                            {loading ? "Signing in..." : "Enter Admin"}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
